import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function ThreadSummary() {
  const [summaryContext, setSummaryContext] = useState('');
  const [threadTitle, setThreadTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState('');
  const { companionId, threadId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchThread();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchThread = async () => {
    try {
      const res = await axios.get(`${API}/threads/${companionId}?token=${token}`);
      const found = res.data.threads.find(t => t.id === threadId);
      if (found) {
        setThreadTitle(found.title || 'conversation');
        setSummaryContext(found.summary_context || '');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const downloadThread = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/threads/${threadId}/export?token=${token}`);
      const blob = new Blob([res.data.text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${threadTitle || 'conversation'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('downloaded ♡');
    } catch (err) {
      console.log(err);
      showToast('something went wrong 🥀');
    }
    setDownloading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/threads/${threadId}/summary`, {
        token,
        summary_context: summaryContext,
      });
      showToast('saved ♡');
    } catch (err) {
      console.log(err);
      showToast('something went wrong 🥀');
    }
    setSaving(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(`/chat/${companionId}/${threadId}`)}>←</button>
        <div style={styles.headerTitleWrap}>
          <div style={styles.headerTitle}>memory</div>
          <div style={styles.headerSub}>{threadTitle} ♡</div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div style={styles.content}>
        <div style={styles.section}>
          <div style={styles.sectionLabel}>export conversation</div>
          <div style={styles.hint}>
            download the full conversation as a text file — useful for asking another AI to write a summary for you
          </div>
          <button
            style={{ ...styles.exportBtn, opacity: downloading ? 0.7 : 1 }}
            onClick={downloadThread}
            disabled={downloading}
          >
            {downloading ? 'preparing…' : 'download conversation ⬇'}
          </button>
        </div>

        <div style={styles.divider} />

        <div style={styles.section}>
          <div style={styles.sectionLabel}>thread summary</div>
          <div style={styles.hint}>
            paste a summary of your conversation history here — gets injected into every message so they always remember what happened before
          </div>
          <textarea
            style={styles.textArea}
            value={summaryContext}
            onChange={e => setSummaryContext(e.target.value)}
            placeholder="paste or write a summary of your roleplay or conversation history here… they'll carry this context into every reply ♡"
          />
        </div>

        <button
          style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
          onClick={save}
          disabled={saving}
        >
          {saving ? 'saving…' : 'save ♡'}
        </button>

        <div style={styles.tipCard}>
          <div style={styles.tipTitle}>how to use this</div>
          <div style={styles.tipText}>
            ask your companion OOC to summarize your conversation so far, copy their response, paste it here and save — they'll remember it in every future message in this thread 🥺
          </div>
        </div>
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '24px 28px 20px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.08)',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexShrink: 0,
  },
  backBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerTitleWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px', color: '#e8ddd0', fontWeight: '300',
  },
  headerSub: {
    fontSize: '11px', color: '#4a3d30',
    fontWeight: '300', marginTop: '2px', textAlign: 'center',
  },
  content: {
    flex: 1, overflowY: 'auto',
    padding: '28px 28px 80px',
    display: 'flex', flexDirection: 'column', gap: '20px',
  },
  section: {
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  sectionLabel: {
    fontSize: '10px', color: '#4a3d30',
    letterSpacing: '0.12em', textTransform: 'uppercase',
    fontWeight: '400', paddingLeft: '2px',
  },
  hint: {
    fontSize: '12px', color: '#4a3d30',
    fontWeight: '300', lineHeight: '1.6', paddingLeft: '2px',
  },
  textArea: {
    background: '#1e1825',
    borderRadius: '16px',
    border: '1px solid rgba(201,147,90,0.1)',
    padding: '16px 18px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '14px', fontWeight: '300',
    color: '#e8ddd0', lineHeight: '1.7',
    width: '100%', minHeight: '240px',
    resize: 'none', outline: 'none',
  },
  saveBtn: {
    width: '100%', padding: '15px',
    borderRadius: '14px', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontFamily: "'DM Sans', sans-serif",
    fontSize: '13px', cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(201,147,90,0.25)',
  },
  tipCard: {
    background: '#1e1825', borderRadius: '16px',
    border: '1px solid rgba(201,147,90,0.08)',
    padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  tipTitle: {
    fontSize: '10px', color: '#4a3d30',
    letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: '400',
  },
  tipText: {
    fontSize: '13px', color: '#7a6a5a',
    fontWeight: '300', lineHeight: '1.6',
  },
  toast: {
    position: 'fixed', bottom: '32px', left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(201,147,90,0.15)',
    border: '1px solid rgba(201,147,90,0.3)',
    color: '#c9935a', padding: '10px 24px',
    borderRadius: '20px', fontSize: '13px',
    whiteSpace: 'nowrap', zIndex: 100,
  },
};

export default ThreadSummary;
